<template>
  <div class="system-log-step" :class="{ 'is-disabled': isDisabled }">
    <div v-if="isDisabled" class="disabled-overlay">
      <p>{{ $t('forwarders.moduleNotSelected') }}</p>
    </div>

    <div class="form-section">
      <!-- Common Configuration -->
      <div class="common-config">
        <div class="field-row">
          <div class="field">
            <label class="label">
              {{ $t('forwarders.timeInterval') }}
              <span class="required-mark">*</span>
              <span 
                class="info-icon clickable" 
                @click="showIntervalTooltip = !showIntervalTooltip"
              >(!)</span>
              <div v-if="showIntervalTooltip" class="tooltip-content">
                <p><strong>{{ $t('forwarders.timeIntervalRules') }}:</strong></p>
                <ul>
                  <li>{{ $t('forwarders.timeIntervalRuleMin') }}</li>
                  <li>{{ $t('forwarders.timeIntervalRuleMax') }}</li>
                </ul>
              </div>
            </label>
            <div class="control">
              <input
                v-model.number="localValue.log_send_interval_seconds"
                class="input"
                :class="{ 'is-danger': intervalError }"
                type="number"
                min="3"
                max="86400"
                :placeholder="$t('forwarders.enterTimeInterval')"
                @input="validateInterval"
                @blur="handleIntervalBlur"
              >
            </div>
            <p v-if="!intervalError" class="help">{{ $t('forwarders.timeIntervalUnit') }} (3 - 86400)</p>
            <p v-if="intervalError" class="help is-danger">{{ intervalError }}</p>
          </div>

          <div class="field">
            <label class="label">
              {{ $t('forwarders.logCount') }}
              <span class="required-mark">*</span>
              <span 
                class="info-icon clickable" 
                @click="showLogCountTooltip = !showLogCountTooltip"
              >(!)</span>
              <div v-if="showLogCountTooltip" class="tooltip-content">
                <p><strong>{{ $t('forwarders.logCountRules') }}:</strong></p>
                <ul>
                  <li>{{ $t('forwarders.logCountRuleMin') }}</li>
                  <li>{{ $t('forwarders.logCountRuleMax') }}</li>
                </ul>
              </div>
            </label>
            <div class="control">
              <input
                v-model.number="localValue.batch_size"
                class="input"
                :class="{ 'is-danger': batchSizeError }"
                type="number"
                min="1"
                max="999999"
                :placeholder="$t('forwarders.enterLogCount')"
                @input="validateBatchSize"
                @blur="handleBatchSizeBlur"
              >
            </div>
            <p v-if="!batchSizeError" class="help">(1 - 999999)</p>
            <p v-if="batchSizeError" class="help is-danger">{{ batchSizeError }}</p>
          </div>
        </div>
      </div>

      <!-- OS Tabs -->
      <div class="os-tabs">
        <div class="tab-headers">
          <div
            class="tab-header"
            :class="{ 'active': activeTab === 'windows' }"
            @click="activeTab = 'windows'"
          >
            <octicon :icon="icons.deviceDesktop" />
            <span>Windows</span>
          </div>
          <div
            class="tab-header"
            :class="{ 'active': activeTab === 'linux' }"
            @click="activeTab = 'linux'"
          >
            <octicon :icon="icons.server" />
            <span>Linux</span>
          </div>
        </div>

        <div class="tab-content">
          <!-- Windows Tab -->
          <div v-if="activeTab === 'windows'" class="tab-pane">
            <div class="enable-checkbox">
              <input
                id="windows-enabled"
                v-model="localValue.windows.enabled"
                type="checkbox"
                class="checkbox"
                :disabled="isDisabled"
              >
              <label for="windows-enabled" class="checkbox-label">
                {{ $t('forwarders.enableWindows') }}
              </label>
            </div>

            <div v-if="localValue.windows.enabled" class="os-config">
              <!-- Index -->
              <div class="field">
                <label class="label">
                  {{ $t('index') }}
                  <span class="required-mark">*</span>
                </label>
                <div class="control">
                  <v-select
                    v-model="localValue.windows.index_id"
                    :options="indexOptions"
                    :reduce="option => option.id"
                    label="name"
                    :placeholder="$t('forwarders.selectIndex')"
                    :filterable="true"
                    :clearable="false"
                  >
                    <template #no-options>
                      {{ $t('forwarders.noIndexAvailable') }}
                    </template>
                  </v-select>
                </div>
              </div>

              <!-- Source Type -->
              <div class="field">
                <label class="label">
                  {{ $t('forwarders.sourceType') }}
                  <span class="required-mark">*</span>
                </label>
                <div class="control">
                  <input
                    :value="getSourceTypeName(localValue.windows.source_type_id, 'windows')"
                    class="input"
                    type="text"
                    disabled
                    readonly
                  >
                </div>
              </div>

              <!-- Channels -->
              <div class="field">
                <label class="label">
                  {{ $t('forwarders.channel') }}
                  <span class="required-mark">*</span>
                  <span 
                    class="info-icon clickable" 
                    @click="showChannelTooltip = !showChannelTooltip"
                  >(?)</span>
                  <div v-if="showChannelTooltip" class="tooltip-content">
                    <p>{{ $t('forwarders.channelHelp') }}</p>
                  </div>
                </label>
                <div class="control">
                  <v-select
                    v-model="localValue.windows.channels"
                    :options="windowsChannels"
                    multiple
                    :placeholder="$t('forwarders.selectChannel')"
                    :close-on-select="false"
                  >
                    <template #no-options>
                      {{ $t('forwarders.noChannelAvailable') }}
                    </template>
                  </v-select>
                </div>
              </div>

              <!-- Custom Path -->
              <div class="field">
                <label class="label">
                  {{ $t('forwarders.customPath') }}
                  <span 
                    class="info-icon clickable" 
                    @click="showCustomPathTooltip = !showCustomPathTooltip"
                  >(?)</span>
                  <div v-if="showCustomPathTooltip" class="tooltip-content">
                    <p>{{ $t('forwarders.customPathHelp') }}</p>
                  </div>
                </label>
                <div class="control">
                  <v-select
                    v-model="localValue.windows.custom_paths"
                    multiple
                    taggable
                    no-drop
                    push-tags
                    :placeholder="$t('forwarders.enterCustomPath')"
                    @option:created="validateCustomPathLength"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Linux Tab -->
          <div v-if="activeTab === 'linux'" class="tab-pane">
            <div class="enable-checkbox">
              <input
                id="linux-enabled"
                v-model="localValue.linux.enabled"
                type="checkbox"
                class="checkbox"
                :disabled="isDisabled"
              >
              <label for="linux-enabled" class="checkbox-label">
                {{ $t('forwarders.enableLinux') }}
              </label>
            </div>

            <div v-if="localValue.linux.enabled" class="os-config">
              <!-- Index -->
              <div class="field">
                <label class="label">
                  {{ $t('index') }}
                  <span class="required-mark">*</span>
                </label>
                <div class="control">
                  <v-select
                    v-model="localValue.linux.index_id"
                    :options="indexOptions"
                    :reduce="option => option.id"
                    label="name"
                    :placeholder="$t('forwarders.selectIndex')"
                    :filterable="true"
                    :clearable="false"
                  >
                    <template #no-options>
                      {{ $t('forwarders.noIndexAvailable') }}
                    </template>
                  </v-select>
                </div>
              </div>

              <!-- Source Type -->
              <div class="field">
                <label class="label">
                  {{ $t('forwarders.sourceType') }}
                  <span class="required-mark">*</span>
                </label>
                <div class="control">
                  <input
                    :value="getSourceTypeName(localValue.linux.source_type_id, 'linux') || $t('forwarders.noSourceTypeAvailable')"
                    class="input"
                    type="text"
                    disabled
                    readonly
                  >
                </div>
              </div>

              <!-- Channels -->
              <div class="field">
                <label class="label">
                  {{ $t('forwarders.channel') }}
                  <span class="required-mark">*</span>
                  <span 
                    class="info-icon clickable" 
                    @click="showChannelTooltip = !showChannelTooltip"
                  >(?)</span>
                  <div v-if="showChannelTooltip" class="tooltip-content">
                    <p>{{ $t('forwarders.channelHelp') }}</p>
                  </div>
                </label>
                <div class="control">
                  <v-select
                    v-model="localValue.linux.channels"
                    :options="linuxChannels"
                    multiple
                    taggable
                    push-tags
                    :placeholder="$t('forwarders.enterChannelPath')"
                    :close-on-select="false"
                    @option:created="validateChannelLength"
                  />
                </div>
              </div>

              <!-- Custom Path -->
              <div class="field">
                <label class="label">
                  {{ $t('forwarders.customPath') }}
                  <span 
                    class="info-icon clickable" 
                    @click="showCustomPathTooltip = !showCustomPathTooltip"
                  >(?)</span>
                  <div v-if="showCustomPathTooltip" class="tooltip-content">
                    <p>{{ $t('forwarders.customPathHelp') }}</p>
                  </div>
                </label>
                <div class="control">
                  <v-select
                    v-model="localValue.linux.custom_paths"
                    multiple
                    taggable
                    no-drop
                    push-tags
                    :placeholder="$t('forwarders.enterCustomPath')"
                    @option:created="validateCustomPathLength"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'
import * as icons from 'octicons-vue'

export default {
  name: 'SystemLogStep',
  components: {
    vSelect
  },
  props: {
    value: {
      type: Object,
      required: true
    },
    indexNames: {
      type: [Object, Array],
      default: () => ({})
    },
    windowsSourceTypes: {
      type: Array,
      default: () => []
    },
    linuxSourceTypes: {
      type: Array,
      default: () => []
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    validationErrors: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      localValue: JSON.parse(JSON.stringify(this.value)),
      activeTab: 'windows',
      intervalError: '',
      batchSizeError: '',
      showIntervalTooltip: false,
      showLogCountTooltip: false,
      showChannelTooltip: false,
      showCustomPathTooltip: false,
      windowsChannels: [
        'Application',
        'Security',
        'System',
        'Setup',
        'Windows PowerShell'
      ],
      linuxChannels: [
        'System logs',
        'Authentication & Security Logs',
        'Alternatives log',
        // 'Service logs',
        // 'Setup logs',
        // 'Network & Firewall Logs',
      ]
    }
  },
  computed: {
    icons() {
      return icons
    },
    defaultLinuxSourceTypeId() {
      return this.linuxSourceTypes[0]?.id || ''
    },
    indexOptions() {
      return Array.isArray(this.indexNames)
        ? this.indexNames
        : Object.values(this.indexNames || {})
    }
  },
  watch: {
    value: {
      handler(newVal) {
        // Avoid deep clone on every change - only update if really needed
        if (JSON.stringify(this.localValue) !== JSON.stringify(newVal)) {
          this.localValue = JSON.parse(JSON.stringify(newVal))
        }
      },
      deep: true
    },
    localValue: {
      handler(newVal) {
        this.$emit('input', newVal)
      },
      deep: true
    },
    'localValue.windows.enabled'(val) {
      if (val && this.windowsSourceTypes.length > 0 && !this.localValue.windows.source_type_id) {
        this.localValue.windows.source_type_id = this.windowsSourceTypes[0].id
      }
    },
    'localValue.linux.enabled'(val) {
      if (val) {
        this.ensureLinuxSourceType()
      }
    },
    linuxSourceTypes: {
      handler() {
        this.ensureLinuxSourceType()
      },
      deep: true
    }
  },
  mounted() {
    // Set default source types
    if (this.windowsSourceTypes.length > 0 && !this.localValue.windows.source_type_id) {
      this.localValue.windows.source_type_id = this.windowsSourceTypes[0].id
    }
    this.ensureLinuxSourceType()
    // Validate initial values
    this.validateInterval()
    this.validateBatchSize()
    // Add click outside listener for tooltips
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    handleClickOutside(event) {
      const tooltipTriggers = this.$el.querySelectorAll('.info-icon.clickable')
      const tooltipContents = this.$el.querySelectorAll('.tooltip-content')
      
      let clickedInsideTooltip = false
      
      tooltipTriggers.forEach(trigger => {
        if (trigger.contains(event.target)) {
          clickedInsideTooltip = true
        }
      })
      
      tooltipContents.forEach(content => {
        if (content.contains(event.target)) {
          clickedInsideTooltip = true
        }
      })
      
      if (!clickedInsideTooltip) {
        this.showIntervalTooltip = false
        this.showLogCountTooltip = false
        this.showChannelTooltip = false
        this.showCustomPathTooltip = false
      }
    },
    getSourceTypeName(sourceTypeId, os) {
      const sourceTypes = os === 'windows' ? this.windowsSourceTypes : this.linuxSourceTypes
      const sourceType = sourceTypes.find(st => st.id === sourceTypeId)
      return sourceType ? sourceType.name : ''
    },
    ensureLinuxSourceType() {
      if (!this.localValue?.linux?.enabled) return
      const hasValidLinuxSourceType = this.linuxSourceTypes.some(sourceType => sourceType.id === this.localValue.linux.source_type_id)
      if (hasValidLinuxSourceType) return
      this.localValue.linux.source_type_id = this.defaultLinuxSourceTypeId
    },
    validateInterval() {
      const value = this.localValue.log_send_interval_seconds

      // Check if value is negative or less than min
      if (value < 3) {
        this.intervalError = this.$t('forwarders.intervalRangeError')
        return false
      }

      // Check if value is greater than max
      if (value > 86400) {
        this.intervalError = this.$t('forwarders.intervalRangeError')
        return false
      }

      this.intervalError = ''
      return true
    },
    handleIntervalBlur() {
      const value = this.localValue.log_send_interval_seconds
      // Reset to default value if empty
      if (!value && value !== 0) {
        this.localValue.log_send_interval_seconds = 600
      } else if (value < 3) {
        // Set to minimum value if less than 3
        this.localValue.log_send_interval_seconds = 3
      } else if (value > 86400) {
        // Set to maximum value if greater than 86400
        this.localValue.log_send_interval_seconds = 86400
      }
      this.intervalError = ''
    },
    validateBatchSize() {
      const value = this.localValue.batch_size

      // Check if value is negative or less than min
      if (value < 1) {
        this.batchSizeError = this.$t('forwarders.batchSizeRangeError')
        return false
      }

      // Check if value is greater than max
      if (value > 999999) {
        this.batchSizeError = this.$t('forwarders.batchSizeRangeError')
        return false
      }

      this.batchSizeError = ''
      return true
    },
    validateCustomPathLength(option) {
      // Limit path length to 4096 characters
      if (option && option.length > 4096) {
        // Remove the last added path if it exceeds limit
        this.$nextTick(() => {
          if (this.activeTab === 'windows') {
            const index = this.localValue.windows.custom_paths.indexOf(option)
            if (index > -1) {
              this.localValue.windows.custom_paths.splice(index, 1)
              this.localValue.windows.custom_paths.push(option.substring(0, 4096))
            }
          } else {
            const index = this.localValue.linux.custom_paths.indexOf(option)
            if (index > -1) {
              this.localValue.linux.custom_paths.splice(index, 1)
              this.localValue.linux.custom_paths.push(option.substring(0, 4096))
            }
          }
        })
      }
    },
    validateChannelLength(option) {
      // Limit channel length to 4096 characters (for Linux taggable channels)
      if (option && option.length > 4096) {
        this.$nextTick(() => {
          const index = this.localValue.linux.channels.indexOf(option)
          if (index > -1) {
            this.localValue.linux.channels.splice(index, 1)
            this.localValue.linux.channels.push(option.substring(0, 4096))
          }
        })
      }
    },
    handleBatchSizeBlur() {
      const value = this.localValue.batch_size
      // Reset to default value if empty
      if (!value && value !== 0) {
        this.localValue.batch_size = 500
      } else if (value < 1) {
        // Set to minimum value if less than 1
        this.localValue.batch_size = 1
      } else if (value > 999999) {
        // Set to maximum value if greater than 999999
        this.localValue.batch_size = 999999
      }
      this.batchSizeError = ''
    },
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/styles/dark-modal.scss';

.system-log-step {
  position: relative;

  &.is-disabled {
    opacity: 0.5;
    pointer-events: none;

    .disabled-overlay {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background-color: rgba(26, 29, 46, 0.95);
      padding: 2rem;
      border-radius: 8px;
      text-align: center;
      z-index: 10;
      pointer-events: auto;

      p {
        color: #fff;
        font-size: 1rem;
        margin: 0;
      }
    }
  }

  .form-section {
    max-width: 700px;
  }

  .common-config {
    margin-bottom: 1.5rem;
    padding: 1rem;
    background-color: rgba(255, 255, 255, 0.03);
    border-radius: 8px;
  }

  .field-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }

  .field {
    margin-bottom: 0.8rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .label {
    color: $modal-text;
    font-weight: 500;
    margin-bottom: 0.35rem;
    display: block;
    position: relative;

    .required-mark {
      color: $modal-required;
      margin-left: 2px;
    }

    .info-icon {
      display: inline-block;
      width: 16px;
      height: 16px;
      line-height: 16px;
      text-align: center;
      border-radius: 50%;
      background-color: rgba(255, 255, 255, 0.1);
      color: #fff;
      font-size: 0.75rem;
      margin-left: 4px;
      font-style: normal;
      transition: all 0.2s;

      &.clickable {
        cursor: pointer;

        &:hover {
          background-color: rgba(255, 255, 255, 0.2);
          transform: scale(1.1);
        }
      }
    }

    .tooltip-content {
      position: absolute;
      top: 100%;
      left: 0;
      margin-top: 0.5rem;
      padding: 1rem;
      background-color: #2a2d36;
      border: 1px solid #404553;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      z-index: 1000;
      min-width: 300px;
      max-width: 400px;

      p {
        color: #fff;
        margin: 0 0 0.5rem 0;
        font-size: 0.9rem;

        strong {
          color: $modal-primary;
        }
      }

      ul {
        list-style: none;
        padding: 0;
        margin: 0;

        li {
          color: rgba(255, 255, 255, 0.85);
          font-size: 0.85rem;
          line-height: 1.6;
          padding-left: 1rem;
          position: relative;
          margin-bottom: 0.25rem;

          &:before {
            content: "•";
            position: absolute;
            left: 0;
            color: $modal-primary;
          }

          &:last-child {
            margin-bottom: 0;
          }
        }
      }
    }
  }

  .control {
    .input,
    .textarea {
      background-color: $modal-input-bg;
      border: 1px solid $modal-border;
      color: $modal-text;
      border-radius: 4px;
      padding: 0.6rem;
      width: 100%;
      font-size: 0.9rem;

      &:focus {
        border-color: $modal-primary;
        outline: none;
      }

      &:disabled,
      &[readonly] {
        background-color: $modal-bg;
        cursor: not-allowed;
        opacity: 0.7;
      }

      &::placeholder {
        color: rgba(255, 255, 255, 0.4);
      }

      &.is-danger {
        border-color: $modal-required;

        &:focus {
          border-color: $modal-required;
          box-shadow: 0 0 0 0.125em rgba(255, 71, 87, 0.25);
        }
      }
    }

    .textarea {
      resize: vertical;
      min-height: 60px;
    }
  }

  .help {
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.8rem;
    margin-top: 0.25rem;

    &.is-danger {
      color: $modal-required;
    }
  }

  .os-tabs {
    .tab-headers {
      display: flex;
      gap: 0.5rem;
      margin-bottom: 1rem;
      border-bottom: 2px solid rgba(255, 255, 255, 0.1);
    }

    .tab-header {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.75rem 1.5rem;
      cursor: pointer;
      color: rgba(255, 255, 255, 0.6);
      font-weight: 500;
      border-bottom: 2px solid transparent;
      margin-bottom: -2px;
      transition: all 0.2s;

      &:hover {
        color: rgba(255, 255, 255, 0.8);
      }

      &.active {
        color: $modal-primary;
        border-bottom-color: $modal-primary;
      }
    }

    .tab-content {
      padding: 1rem;
      background-color: rgba(255, 255, 255, 0.03);
      border-radius: 8px;
    }

    .tab-pane {
      .enable-checkbox {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        margin-bottom: 1rem;
        padding-bottom: 1rem;
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);

        .checkbox {
          width: 18px;
          height: 18px;
          cursor: pointer;
          accent-color: $modal-primary;
        }

        .checkbox-label {
          color: $modal-text;
          font-size: 1rem;
          font-weight: 500;
          cursor: pointer;
          margin: 0;
        }
      }

      .os-config {
        padding-top: 0.5rem;
      }
    }
  }
}
</style>
